import { Injectable, signal } from '@angular/core';
import { MenuItem } from 'primeng/api';

const PATH_LABELS: Record<string, string> = {
  'identity-providers': 'Identity Providers',
  'identity-providers/add': 'Add Identity Provider',
  organizations: 'Organizations',
  'organizations/add': 'Add Organization',
  'organizations/edit': 'Edit Organization',
  applications: 'Applications',
  'applications/add': 'Add Application',
  users: 'Users',
  'users/add': 'Add User',
  'users/edit': 'Edit User',
  roles: 'Roles',
  sessions: 'Sessions',
  settings: 'Settings',
};

export interface AdminBreadcrumbPageLabel {
  path: string;
  label: string;
}

function cleanAdminPath(url: string): string {
  return url.split('?')[0].replace(/^\/admin\/?/, '');
}

function toTitleCase(segment: string): string {
  return segment
    .split('-')
    .map((value) => value.charAt(0).toUpperCase() + value.slice(1))
    .join(' ');
}

export function buildAdminBreadcrumbs(
  url: string,
  pageLabel: AdminBreadcrumbPageLabel | null = null
): MenuItem[] {
  const cleanUrl = cleanAdminPath(url);

  if (!cleanUrl) {
    return [];
  }

  const segments = cleanUrl.split('/').filter(Boolean);
  const items: MenuItem[] = [];
  let accumulatedPath = '/admin';

  for (let index = 0; index < segments.length; index += 1) {
    accumulatedPath += `/${segments[index]}`;
    const combinedKey = segments.slice(0, index + 1).join('/');
    const isLast = index === segments.length - 1;
    const hasDynamicLabel = isLast && pageLabel?.path === cleanUrl;
    const label = hasDynamicLabel
      ? pageLabel.label
      : (PATH_LABELS[combinedKey] ?? toTitleCase(segments[index]));

    items.push({
      label,
      ...(isLast ? {} : { routerLink: accumulatedPath }),
    });
  }

  return items;
}

@Injectable({ providedIn: 'root' })
export class AdminBreadcrumbService {
  private readonly pageLabelState = signal<AdminBreadcrumbPageLabel | null>(null);
  readonly pageLabel = this.pageLabelState.asReadonly();

  setPageLabel(url: string, label: string): void {
    const normalizedLabel = label.trim();

    this.pageLabelState.set(
      normalizedLabel ? {
        path: cleanAdminPath(url),
        label: normalizedLabel,
      } : null
    );
  }
}
